import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { staffChatApi } from "@/api";
import { Button, Spinner, Toast, useToast } from "@/components/ui";
import { formatDate } from "@/lib/format";
import { formatBytes } from "@/lib/shares";
import { TaskDrawer } from "@/staff/tasks/TaskDrawer";
import type { ChatAttachment, ChatJob, ChatMessage } from "@/types";
import { useAuth } from "@/staff/auth/AuthContext";
import { useChatFeed } from "./ChatContext";
import { ChatSidebar } from "./ChatSidebar";
import { MessageAttachments } from "./MessageAttachments";
import { LinkedText } from "./LinkedText";
import "./chat.css";

/** Chat › Messages (§4.3): job list left, day-grouped thread right. Opening a
 *  job calls the thread endpoint (which marks it read server-side), then zeros
 *  the sidebar badge locally. Open thread re-polls every 15s while visible.
 *  Composer: Enter sends, Shift+Enter newline; on failure the text stays in
 *  the composer with an inline error and manual retry (no outbox — §4.3).
 *  Attachments: picked files upload immediately as staged chips; the send
 *  carries only their ids, so a retried send never re-uploads bytes.
 *  Edit / delete (2026-09-09): own staff messages only, keyed on sender_user_id
 *  (a colleague's message is theirs). Edit is inline in place of the bubble,
 *  text only; delete is a confirm then the message vanishes — for the client
 *  too, on their next poll (server soft-delete, no tombstone). The 15s poll
 *  replaces the thread wholesale, so both propagate to other consoles by
 *  themselves; the open editor is keyed by message id and survives a poll. */

const THREAD_POLL_MS = 15_000;
const MAX_ATTACHMENTS = 4;
const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

/** One picked file in the composer. Staged uploads are job-bound, so chips are
 *  cleared on job switch (an abandoned staged upload is harmless). */
interface AttachmentChip {
  key: number;
  file: File;
  status: "uploading" | "ready" | "failed";
  /** Set once uploaded (status "ready"). */
  uploaded?: ChatAttachment;
  /** Set on failure — the server's message verbatim where it rejected. */
  error?: string;
}

export function MessagesPage() {
  const { jobs, refresh, markReadLocally, selectedJobId, setSelectedJobId } = useChatFeed();
  const selected = useMemo(
    () => jobs?.find((j) => j.job_id === selectedJobId) ?? null,
    [jobs, selectedJobId],
  );

  const [messages, setMessages] = useState<ChatMessage[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [chips, setChips] = useState<AttachmentChip[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  // Snapshot of the message + its job taken at click time, so the open drawer
  // survives both the 15s poll (which replaces `messages` wholesale) and a
  // sidebar job switch.
  const [taskSource, setTaskSource] = useState<{ message: ChatMessage; job: ChatJob } | null>(null);
  // Inline edit of one own message: the draft lives here (not in `messages`)
  // so a poll replacing the thread can't wipe half-typed text.
  const [editing, setEditing] = useState<{ id: number; draft: string } | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const { user } = useAuth();
  const myId = user?.id ?? 0;
  const { toast, showToast } = useToast();
  const editRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const nextChipKey = useRef(1);

  const loadThread = useCallback(
    async (jobId: number, opts?: { silent?: boolean; signal?: AbortSignal }) => {
      try {
        const msgs = await staffChatApi.listMessages(jobId, opts?.signal);
        setMessages(msgs);
        setLoadError(null);
        // Server marked the job read — clear the badges without waiting for the poll.
        markReadLocally(jobId);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (!opts?.silent) {
          setLoadError(err instanceof Error ? err.message : "Could not load the conversation.");
        }
      }
    },
    [markReadLocally],
  );

  // Load on selection change.
  useEffect(() => {
    if (!selectedJobId) {
      setMessages(null);
      return;
    }
    setMessages(null);
    setLoadError(null);
    setSendError(null);
    // Staged uploads belong to the job they were uploaded on — never carry
    // chips across a job switch (the send would fail "Invalid attachment
    // reference"; the abandoned staged rows are harmless).
    setChips([]);
    setAttachError(null);
    setEditing(null);
    setEditError(null);
    setDeletingId(null);
    const ctrl = new AbortController();
    void loadThread(selectedJobId, { signal: ctrl.signal });
    return () => ctrl.abort();
  }, [selectedJobId, loadThread]);

  // Open-thread polling, paused on hidden tab (§4.3).
  useEffect(() => {
    if (!selectedJobId) return;
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (!interval) {
        interval = setInterval(() => void loadThread(selectedJobId, { silent: true }), THREAD_POLL_MS);
      }
    };
    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        void loadThread(selectedJobId, { silent: true });
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [selectedJobId, loadThread]);

  // The thread renders column-reverse (newest day group first in the DOM), so
  // the browser's scroll origin IS the bottom: every thread opens at its newest
  // message with no JS, stays glued there while image thumbnails load in
  // (growth extends the thread upward), and a reader who scrolled up isn't
  // yanked down by the 15s poll (their offset from the bottom is preserved).
  // The only scripted scroll left is resetting to the bottom on a job switch,
  // in case the previous thread was left scrolled up.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = 0; // 0 = the bottom in a column-reverse scroller
  }, [selectedJobId]);

  // Focus the inline editor when it opens, caret at the end of the text.
  const editingId = editing?.id ?? null;
  useEffect(() => {
    if (editingId === null) return;
    const el = editRef.current;
    if (el) {
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [editingId]);

  const dayGroups = useMemo(() => {
    const out: { day: string; msgs: ChatMessage[] }[] = [];
    for (const m of messages ?? []) {
      const day = formatDate(m.created_at);
      const last = out[out.length - 1];
      if (last && last.day === day) last.msgs.push(m);
      else out.push({ day, msgs: [m] });
    }
    return out;
  }, [messages]);

  // Read receipt: the newest staff message the client side has seen. The portal's
  // thread fetch stamps every unread staff message at once, so the seen set is a
  // prefix of the staff messages — one caption at the frontier says "everything up
  // to here was seen". Side-level, not per-person: with two portal users on a job
  // the first reader stamps it, hence "Seen", never "Seen by …".
  const seenFrontierId = useMemo(() => {
    let id: number | null = null;
    for (const m of messages ?? []) {
      if (m.sender_type === "staff" && (m.client_read_at ?? 0) > 0) id = m.id;
    }
    return id;
  }, [messages]);

  const hasUploading = chips.some((c) => c.status === "uploading");
  const hasFailed = chips.some((c) => c.status === "failed");
  const readyIds = chips
    .filter((c) => c.status === "ready" && c.uploaded)
    .map((c) => c.uploaded!.attachment_id);

  // Text OR ≥1 attachment; a failed chip must be retried or removed first —
  // never silently drop a file from a sent message.
  const canSend =
    (draft.trim().length > 0 || readyIds.length > 0) &&
    !hasUploading &&
    !hasFailed &&
    !sending &&
    !!selectedJobId;

  const uploadChip = useCallback(async (key: number, file: File, jobId: number) => {
    try {
      const uploaded = await staffChatApi.uploadAttachment(jobId, file);
      setChips((prev) => prev.map((c) => (c.key === key ? { ...c, status: "ready", uploaded } : c)));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed.";
      setChips((prev) =>
        prev.map((c) => (c.key === key ? { ...c, status: "failed", error: message } : c)),
      );
    }
  }, []);

  function onPickFiles(e: ChangeEvent<HTMLInputElement>) {
    const input = e.currentTarget;
    const files = Array.from(input.files ?? []);
    input.value = ""; // so re-picking the same file fires onChange again
    if (!selectedJobId || files.length === 0) return;

    // Pre-check for instant feedback (the server enforces the same limits, and
    // its messages are surfaced verbatim on the chips if anything slips past).
    const problems: string[] = [];
    const accepted: AttachmentChip[] = [];
    let slots = MAX_ATTACHMENTS - chips.length;
    for (const file of files) {
      if (!(file.type.startsWith("image/") || file.type === "application/pdf")) {
        problems.push(`${file.name}: Only images and PDFs can be attached.`);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        problems.push(`${file.name}: Files can be up to 20 MB.`);
        continue;
      }
      if (slots <= 0) {
        problems.push(`${file.name}: A message can carry up to ${MAX_ATTACHMENTS} files.`);
        continue;
      }
      slots -= 1;
      accepted.push({ key: nextChipKey.current++, file, status: "uploading" });
    }
    setAttachError(problems.length ? problems.join(" ") : null);
    if (accepted.length) {
      setChips((prev) => [...prev, ...accepted]);
      for (const chip of accepted) void uploadChip(chip.key, chip.file, selectedJobId);
    }
  }

  function retryChip(chip: AttachmentChip) {
    if (!selectedJobId) return;
    setChips((prev) =>
      prev.map((c) => (c.key === chip.key ? { ...c, status: "uploading", error: undefined } : c)),
    );
    void uploadChip(chip.key, chip.file, selectedJobId);
  }

  function removeChip(key: number) {
    setChips((prev) => prev.filter((c) => c.key !== key));
  }

  async function send() {
    if (!canSend || !selectedJobId) return;
    setSending(true);
    setSendError(null);
    try {
      const msg = await staffChatApi.sendMessage(selectedJobId, {
        body: draft.trim(),
        attachmentIds: readyIds,
      });
      setDraft("");
      setChips([]);
      setAttachError(null);
      setMessages((prev) => (prev ? [...prev, msg] : [msg]));
      // Sending always returns you to the bottom, even if you'd scrolled up.
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      void refresh(); // snippet + ordering in the sidebar
    } catch (err) {
      // Text and chips stay in the composer; inline error + manual retry
      // (§4.3). The retry reuses the uploaded ids — bytes never re-upload.
      setSendError(err instanceof Error ? err.message : "The message could not be sent.");
    } finally {
      setSending(false);
    }
  }

  function startEdit(m: ChatMessage) {
    setEditing({ id: m.id, draft: m.body });
    setEditError(null);
  }

  function cancelEdit() {
    setEditing(null);
    setEditError(null);
  }

  async function saveEdit() {
    if (!editing || !selectedJobId || editSaving) return;
    const original = messages?.find((m) => m.id === editing.id);
    const next = editing.draft.trim();
    if (original && next === original.body) {
      cancelEdit(); // nothing changed — don't stamp "edited" for a no-op
      return;
    }
    if (!next && !(original && original.attachments.length > 0)) {
      setEditError("A message needs some text.");
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await staffChatApi.editMessage(selectedJobId, editing.id, next);
      setMessages((prev) =>
        prev ? prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)) : prev,
      );
      setEditing(null);
      void refresh(); // the sidebar snippet may be this very message
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "The message could not be saved.");
    } finally {
      setEditSaving(false);
    }
  }

  function onEditorKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void saveEdit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  }

  async function removeMessage(m: ChatMessage) {
    if (!selectedJobId || deletingId !== null) return;
    if (
      !window.confirm(
        "Delete this message? It disappears for the client too, and can't be undone.",
      )
    ) {
      return;
    }
    setDeletingId(m.id);
    try {
      await staffChatApi.deleteMessage(selectedJobId, m.id);
      setMessages((prev) => (prev ? prev.filter((x) => x.id !== m.id) : prev));
      if (editing?.id === m.id) cancelEdit();
      showToast("Message deleted");
      void refresh(); // snippet + ordering in the sidebar
    } catch (err) {
      showToast(err instanceof Error ? err.message : "The message could not be deleted.", "warn");
    } finally {
      setDeletingId(null);
    }
  }

  function onComposerKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  return (
    <>
      <div className="chat-split">
        <ChatSidebar
          jobs={jobs}
          selectedJobId={selectedJobId}
          onSelect={setSelectedJobId}
          showSnippets
        />
  
        <section className="chat-main" aria-label="Conversation">
          {!selected ? (
            <p className="chat-main__empty">Select a job to view its conversation.</p>
          ) : (
            <>
              <header className="chat-main__head">
                <h2 className="chat-main__title">
                  {selected.job_number > 0 ? `${selected.job_number} · ` : ""}
                  {selected.name}
                </h2>
                <span className="chat-main__subtitle">{selected.client_names}</span>
              </header>
  
              <div
                className={`chat-thread${messages && messages.length > 0 ? " chat-thread--reversed" : ""}`}
                ref={scrollRef}
              >
                {loadError ? (
                  <div className="chat-thread__error" role="alert">
                    <p>{loadError}</p>
                    <Button variant="secondary" size="sm" onClick={() => void loadThread(selected.job_id)}>
                      Try again
                    </Button>
                  </div>
                ) : messages === null ? (
                  <div className="chat-thread__loading">
                    <Spinner />
                  </div>
                ) : messages.length === 0 ? (
                  <p className="chat-thread__empty">
                    Start of the conversation — anything sent here reaches the client's portal app.
                  </p>
                ) : (
                  // Newest group first in the DOM — column-reverse flips the
                  // visual order back to chronological. Messages inside a group
                  // stay in DOM order (.chat-day is a normal column).
                  [...dayGroups].reverse().map((g) => (
                    <div key={g.day} className="chat-day">
                      <div className="chat-day__divider">
                        <span>{g.day}</span>
                      </div>
                      {g.msgs.map((m) => {
                        // "Mine" keys on the author id, never on sender_type alone.
                        const mine =
                          m.sender_type === "staff" &&
                          (m.sender_user_id ?? 0) > 0 &&
                          m.sender_user_id === myId;
                        const edit = editing && editing.id === m.id ? editing : null;
                        return (
                          <div
                            key={m.id}
                            className={`chat-msg${m.sender_type === "staff" ? " chat-msg--staff" : ""}${m.sender_type === "builder" ? " chat-msg--builder" : ""}`}
                          >
                            <span className="chat-msg__meta">
                              <span className="chat-msg__name">{m.sender_name}</span>
                              {m.sender_type === "builder" ? (
                                <span className="chat-msg__role" title="A builder on this job">Builder</span>
                              ) : null}
                              <span className="chat-msg__time">{timeLabel(m.created_at)}</span>
                              {(m.edited_at ?? 0) > 0 ? (
                                <span
                                  className="chat-msg__edited"
                                  title={`Edited ${editedLabel(m.edited_at ?? 0)}`}
                                >
                                  edited
                                </span>
                              ) : null}
                              {mine && !edit ? (
                                <>
                                  <button
                                    type="button"
                                    className="chat-msg__action"
                                    onClick={() => startEdit(m)}
                                    title="Edit this message"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="chat-msg__action chat-msg__action--danger"
                                    onClick={() => void removeMessage(m)}
                                    disabled={deletingId === m.id}
                                    title="Delete this message"
                                  >
                                    Delete
                                  </button>
                                </>
                              ) : null}
                              <button
                                type="button"
                                className="chat-msg__action"
                                onClick={() => setTaskSource({ message: { ...m }, job: selected })}
                                title="Create a task from this message"
                              >
                                + Task
                              </button>
                            </span>
                            {edit ? (
                              <div className="chat-msg__editor">
                                <textarea
                                  ref={editRef}
                                  className="chat-msg__editor-input"
                                  value={edit.draft}
                                  onChange={(e) => setEditing({ id: m.id, draft: e.target.value })}
                                  onKeyDown={onEditorKeyDown}
                                  rows={Math.min(8, Math.max(2, edit.draft.split("\n").length))}
                                  disabled={editSaving}
                                  aria-label="Edit message"
                                />
                                {editError ? (
                                  <span className="chat-msg__editor-error" role="alert">
                                    {editError}
                                  </span>
                                ) : null}
                                <div className="chat-msg__editor-row">
                                  <span className="chat-msg__editor-hint">
                                    Enter to save · Esc to cancel
                                  </span>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={cancelEdit}
                                    disabled={editSaving}
                                  >
                                    Cancel
                                  </Button>
                                  <Button size="sm" onClick={() => void saveEdit()} loading={editSaving}>
                                    Save
                                  </Button>
                                </div>
                              </div>
                            ) : m.body ? (
                              // Attachment-only messages have no bubble at all.
                              <span className="chat-msg__bubble">
                                <LinkedText text={m.body} />
                              </span>
                            ) : null}
                            {m.attachments.length > 0 ? (
                              <MessageAttachments attachments={m.attachments} />
                            ) : null}
                            {m.id === seenFrontierId ? (
                              <span className="chat-msg__seen">
                                Seen {seenLabel(m.client_read_at ?? 0)}
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
  
              <footer className="chat-composer">
                {sendError ? (
                  <div className="chat-composer__error" role="alert">
                    <span>{sendError}</span>
                    <Button variant="secondary" size="sm" onClick={() => void send()} disabled={sending}>
                      Retry
                    </Button>
                  </div>
                ) : null}
                {attachError ? (
                  <div className="chat-composer__error" role="alert">
                    <span>{attachError}</span>
                  </div>
                ) : null}
                {chips.length > 0 ? (
                  <ul className="chat-chips">
                    {chips.map((chip) => (
                      <li
                        key={chip.key}
                        className={`chat-chip${chip.status === "failed" ? " is-failed" : ""}`}
                      >
                        {chip.status === "uploading" ? (
                          <span className="chat-mini-spinner" aria-hidden="true" />
                        ) : null}
                        {chip.status === "failed" ? (
                          <button type="button" className="chat-chip__body" onClick={() => retryChip(chip)}>
                            <span className="chat-chip__name">{chip.file.name}</span>
                            <span className="chat-chip__note">
                              {chip.error || "Failed"} — click to retry
                            </span>
                          </button>
                        ) : (
                          <span className="chat-chip__body">
                            <span className="chat-chip__name">{chip.file.name}</span>
                            <span className="chat-chip__note">
                              {formatBytes(chip.file.size)}
                              {chip.status === "uploading" ? " · uploading…" : ""}
                            </span>
                          </span>
                        )}
                        <button
                          type="button"
                          className="chat-chip__remove"
                          onClick={() => removeChip(chip.key)}
                          aria-label={`Remove ${chip.file.name}`}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <div className="chat-composer__row">
                  <button
                    type="button"
                    className="chat-composer__attach"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={chips.length >= MAX_ATTACHMENTS}
                    aria-label="Attach files"
                    title="Attach images or PDFs (up to 4, 20 MB each)"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l8.57-8.57a4 4 0 1 1 5.66 5.66l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    onChange={onPickFiles}
                    hidden
                  />
                  <textarea
                    className="chat-composer__input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onComposerKeyDown}
                    placeholder="Message the client…"
                    rows={2}
                    aria-label="Message"
                  />
                  <Button onClick={() => void send()} disabled={!canSend} loading={sending}>
                    Send
                  </Button>
                </div>
                <p className="chat-composer__hint">Enter to send · Shift+Enter for a new line</p>
              </footer>
            </>
          )}
        </section>
      </div>

      {taskSource ? (
        <TaskDrawer
          task={null}
          initial={{
            job: {
              id: taskSource.job.job_id,
              label:
                (taskSource.job.job_number > 0 ? `#${taskSource.job.job_number} · ` : "") +
                taskSource.job.name,
            },
            title: taskTitleFromMessage(taskSource.message),
            description: taskDescriptionFromMessage(taskSource.message),
            chatMessageId: taskSource.message.id,
          }}
          onClose={() => setTaskSource(null)}
          onSaved={() => {
            setTaskSource(null);
            showToast("Task created");
          }}
        />
      ) : null}
      <Toast toast={toast} />
    </>
  );
}

/** In-bubble time (the day is carried by the group divider). */
function timeLabel(ms: number): string {
  return new Date(ms).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/London",
  });
}

/** The receipt caption's "when" — the staff app's semantics: clock time today,
 *  short weekday inside a week, short date beyond (the read may be days newer
 *  than the message, so the bubble's own day divider says nothing about it). */
function seenLabel(ms: number): string {
  const d = new Date(ms);
  const sameDay = d.toDateString() === new Date().toDateString();
  if (sameDay) return timeLabel(ms);
  const diffDays = Math.floor((Date.now() - ms) / 86400000);
  if (diffDays < 7) {
    return d.toLocaleDateString("en-GB", { weekday: "short", timeZone: "Europe/London" });
  }
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "Europe/London" });
}

/** The "(edited)" tooltip: full date + clock time, London. */
function editedLabel(ms: number): string {
  return `${formatDate(ms)} ${timeLabel(ms)}`;
}

/** Drawer title seed: the message's first line, clipped; attachment-only
 *  messages fall back to a generic follow-up title. */
function taskTitleFromMessage(m: ChatMessage): string {
  const firstLine = (m.body.split("\n")[0] ?? "").trim();
  if (!firstLine) return `Follow up: message from ${m.sender_name}`;
  return firstLine.length > 80 ? `${firstLine.slice(0, 79).trimEnd()}…` : firstLine;
}

/** Drawer description seed: provenance line + the quoted message + any
 *  attachment names, so the task reads standalone off the board. */
function taskDescriptionFromMessage(m: ChatMessage): string {
  const lines = [`From chat — ${m.sender_name}, ${formatDate(m.created_at)} ${timeLabel(m.created_at)}:`];
  const body = m.body.trim();
  if (body) lines.push(`“${body}”`);
  if (m.attachments.length > 0) {
    lines.push(`Attachments: ${m.attachments.map((a) => a.file_name).join(", ")}`);
  }
  return lines.join("\n");
}